/**
 * PerfOverlayView — 좌상단 고정 성능 텍스트 오버레이 (Humble View).
 *
 * 로직 없음 — render(lines)로 받은 문자열만 표시. scrollFactor 0 + 최상단 depth + 입력 미차단.
 * enabled=false면 숨김. 색상/위치는 opts로 주입(앱 테마 토큰 전달).
 */
import type Phaser from "phaser";

export interface PerfOverlayOptions {
  /** Phaser display-list 계측에서 perf overlay를 구분할 이름. */
  name?: string;
  x?: number;
  y?: number;
  depth?: number;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  /** text.setText 최소 간격 ms. 0이면 매 render 호출마다 반영. 기본 0. */
  updateIntervalMs?: number;
  /** 시간 소스. 테스트 주입용. */
  now?: () => number;
}

export interface PerfOverlay {
  setEnabled(on: boolean): void;
  isEnabled(): boolean;
  render(lines: string[]): void;
  /** update interval이 지나 실제 갱신할 때만 lines를 만든다. */
  renderLazy(lines: () => string[]): void;
  destroy(): void;
}

export function createPerfOverlay(scene: Phaser.Scene, opts: PerfOverlayOptions = {}): PerfOverlay {
  const text = scene.add.text(opts.x ?? 6, opts.y ?? 6, "", {
    fontFamily: "monospace",
    fontSize: `${opts.fontSize ?? 11}px`,
    color: opts.color ?? "#7CFC9A",
    backgroundColor: opts.backgroundColor ?? "rgba(0,0,0,0.55)",
    padding: { x: 4, y: 3 },
  });
  text.setName(opts.name ?? "perfOverlayText");
  text.setScrollFactor(0);
  text.setDepth(opts.depth ?? 1_000_000); // 모든 UI 위 — DIAG 도구라 항상 보이게
  text.setVisible(false);
  // 오버레이가 게임/버튼 입력을 가로채지 않게 — 표시 전용.
  if (text.input) text.input.enabled = false;

  let enabled = false;
  let lastRenderAt = Number.NEGATIVE_INFINITY;
  let lastText = "";
  const updateIntervalMs = opts.updateIntervalMs ?? 0;
  const now =
    opts.now ??
    (() =>
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now());

  function renderLazy(lines: () => string[]): void {
    if (!enabled) return;
    const t = now();
    if (t - lastRenderAt < updateIntervalMs) return;
    const nextLines = lines();
    const nextText = nextLines.join("\n");
    if (nextText === lastText) return;
    lastRenderAt = t;
    lastText = nextText;
    text.setText(nextLines);
  }

  return {
    setEnabled(on: boolean): void {
      enabled = on;
      text.setVisible(on);
    },
    isEnabled: () => enabled,
    render(lines: string[]): void {
      renderLazy(() => lines);
    },
    renderLazy,
    destroy(): void {
      text.destroy();
    },
  };
}
